import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import theme from '@codex-brightworld/themes';

const IndicatorWrapper = styled.div`
  display: flex;
  margin: 10px 0;
  justify-content: space-between
`;

const IndicatorBar = styled.span`
  display: inline-block;
  flex-grow: 1;
  height: 4px;
  margin: 0;
  border-radius: 2px;
  background-color: ${props => props.color || props.theme.colors.neutral.grey[3]};
  transition: background-color 0.3s;
`;

const Indicator = (props) => {
  const level = props.bars.filter(config => config.level === props.level);
  const color = level.length ? level[0].color : undefined;
  return (
    <IndicatorWrapper>
      {props.bars.map(config => (
        <IndicatorBar
          key={config.level}
          color={config.level <= props.level ? color : undefined}
        />
      ))}
    </IndicatorWrapper>
  );
};

Indicator.propTypes = {
  level: PropTypes.number.isRequired,
  bars: PropTypes.arrayOf(PropTypes.shape({
    level: PropTypes.number,
    color: PropTypes.string
  })).isRequired
};

const Message = styled.div`
  color: ${props => props.color || props.theme.colors.neutral.grey[3]};
  font-size: 12px;
`;

const ExtraMessage = styled.div`
  color: ${props => props.theme.colors.neutral.grey[4]};
  font-size: 12px;
  padding-top: 10px;
`;

Message.propTypes = {
  color: PropTypes.string
};

/**
 * Return the first config that matches the regExp.
 * @param password
 * @param strengthConfigs
 * @return {object}
 */
const useStrength = (password, strengthConfigs) => useMemo(
  () => strengthConfigs.find(config => config.regExp.test(password)) || {},
  [password, strengthConfigs]
);

const getOrderedConfigs = strengthConfigs => [...strengthConfigs]
  .sort((c1, c2) => c2.level - c1.level)
  .map(config => ({
    ...config,
    regExp: new RegExp(config.regExpString.replace('MIN_LENGTH', config.minLength || 0))
  }));

const getStrengthBars = (strengthConfigs) => {
  const strengthBars = strengthConfigs.map(config => ({
    color: config.color,
    level: config.level
  }));
  strengthBars.shift();
  return strengthBars;
};

const PasswordStrength = (props) => {
  const [strengthConfigs, setStrengthConfigs] = useState(getOrderedConfigs(props.strengthConfigs));
  const [bars, setBars] = useState(getStrengthBars(props.strengthConfigs));
  const { level, color, message } = useStrength(props.password, strengthConfigs);

  useEffect(() => {
    setStrengthConfigs(getOrderedConfigs(props.strengthConfigs));
    setBars(getStrengthBars(props.strengthConfigs));
  }, [props.strengthConfigs]);

  return (
    <div data-component="PasswordStrength" data-qa={props.qa}>
      {props.renderIndicator(Indicator, { bars, level })}
      {props.password.length > 0 && props.renderMessage(Message, { color, message })}
      <ExtraMessage>Include upper case letters, numbers and special characters to increase strength.</ExtraMessage>
    </div>
  );
};

PasswordStrength.propTypes = {
  password: PropTypes.string.isRequired,

  /**
   * Renders indicator component.
   * @param DefaultIndicator
   * @param bars
   * @param level
   * @return {React.Node}
   */
  renderIndicator: PropTypes.func,

  /**
   * Renders message component.
   * @param DefaultMessage
   * @param color
   * @param message
   * @return {React.Node}
   */
  renderMessage: PropTypes.func,

  /**
   * List of configurations for each bar.
   * First item (index 0) is for insufficient password (no strength is displayed, just empty bars).
   * Items with index from 1 to N represents visual bars.
   * For easier modification of default config you can just merge
   * your (partial) config with the default one below.
   * MIN_LENGTH in regexp string will be replaced by `minLength` value.
   */
  strengthConfigs: PropTypes.arrayOf(PropTypes.shape({
    level: PropTypes.number.isRequired,
    color: PropTypes.string,
    message: PropTypes.string,
    minLength: PropTypes.number,
    regExpString: PropTypes.string.isRequired
  })),
  qa: PropTypes.string
};

PasswordStrength.defaultProps = {
  renderIndicator: (DefaultIndicator, { bars, level }) => (
    <DefaultIndicator bars={bars} level={level} />
  ),
  renderMessage: (DefaultMessage, { color, message }) => (
    <DefaultMessage color={color}>{message}</DefaultMessage>
  ),
  strengthConfigs: [
    {
      level: 0,
      color: theme.brightworld.colors.neutral.grey[4],
      message: 'Very weak',
      regExpString: '.*'
    },
    {
      level: 1,
      color: theme.brightworld.colors.semantic.red,
      message: 'Weak',
      minLength: 6,
      regExpString: '^[a-zA-Z0-9\\W_]{MIN_LENGTH,}$'
    },
    {
      level: 2,
      color: theme.brightworld.colors.semantic.starOrange,
      message: 'OK',
      minLength: 8,
      regExpString: '^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9]).{MIN_LENGTH,}$'
    },
    {
      level: 3,
      color: theme.brightworld.colors.semantic.orange,
      message: 'Strong',
      minLength: 10,
      regExpString: '^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9\\W_]).{MIN_LENGTH,}$'
    },
    {
      level: 4,
      color: theme.brightworld.colors.semantic.green,
      message: 'Very strong',
      minLength: 10,
      regExpString: '^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[\\W_]).{MIN_LENGTH,}$'
    }
  ],
  qa: undefined
};

export default PasswordStrength;
